/**
 * Toast notifications.
 *
 * Deliberately minimal: success and error messages after an action, with an
 * accessible live region so a screen reader announces them. Errors stay until
 * dismissed, because an error a user did not see is worse than a lingering
 * toast; successes auto-dismiss.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/Icon.jsx';

const TOAST_ICONS = { success: 'success', error: 'error', info: 'info', warning: 'warning' };

const ToastContext = globalThis.__TOAST_CONTEXT__ || (globalThis.__TOAST_CONTEXT__ = createContext(null));

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (message, { type = 'info', duration } = {}) => {
      const id = (nextId += 1);
      // Errors are sticky by default; anything else clears itself.
      const ttl = duration ?? (type === 'error' ? 0 : 4000);

      setToasts((current) => [...current, { id, message, type }]);

      if (ttl > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ttl)
        );
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      toasts,
      dismiss,
      push,
      success: (message, options) => push(message, { ...options, type: 'success' }),
      /** Accepts an ApiError or a string, and appends the request id if present. */
      error: (error, options) => {
        const message = typeof error === 'string' ? error : error?.message ?? 'Something went wrong';
        const requestId = typeof error === 'object' ? error?.requestId : null;
        return push(requestId ? `${message} (ref ${String(requestId).slice(0, 8)})` : message, {
          ...options,
          type: 'error',
        });
      },
      info: (message, options) => push(message, { ...options, type: 'info' }),
      warning: (message, options) => push(message, { ...options, type: 'warning' }),
    }),
    [toasts, dismiss, push]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider');
  return context;
}

/** Rendered once, near the root. */
export function ToastRegion() {
  const { toasts, dismiss } = useToast();
  if (!toasts.length) return null;

  return (
    <div className="toast-region" role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast toast-${toast.type}`}
          role={toast.type === 'error' ? 'alert' : 'status'}
          aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
        >
          <span className="toast-icon" aria-hidden="true">
            <Icon name={TOAST_ICONS[toast.type] ?? 'info'} size={17} />
          </span>
          <div className="toast-body">{toast.message}</div>
          <button type="button" className="toast-close" onClick={() => dismiss(toast.id)} aria-label="Dismiss">
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
